import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { api, BASE } from "../../lib/api";
import { Reveal, Eyebrow } from "./Reveal";
import { useContent, pick } from "../../lib/content";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";

const Photo = ({ src, label, className, testid }) => (
  <motion.figure whileHover="hover" className={`group relative overflow-hidden rounded-3xl border border-amber-900/40 ${className}`} data-testid={testid}>
    <motion.img src={src} alt={label} variants={{ hover: { scale: 1.07 } }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908] via-[#0a0908]/20 to-transparent" />
    <figcaption className="absolute bottom-5 left-5 right-5 font-serif-it text-xl italic text-amber-100 md:text-2xl">{label}</figcaption>
  </motion.figure>
);

const fileUrl = (m) => `${BASE}/api/files/${m.storage_path}`;

const MediaTile = ({ m, onOpen }) => (
  <button onClick={() => onOpen(m)} data-testid={`media-tile-${m.id}`}
    className={`group relative overflow-hidden rounded-3xl border border-amber-900/40 text-left ${m.type === "photo" ? "md:col-span-2" : ""} auto-rows-[240px] h-[240px] md:h-auto`}>
    {m.type === "photo" ? (
      <motion.img src={fileUrl(m)} alt={m.title} variants={{ hover: { scale: 1.07 } }} transition={{ duration: 1.1 }}
        className="absolute inset-0 h-full w-full object-cover" />
    ) : (
      <video src={fileUrl(m)} muted playsInline preload="metadata"
        className="absolute inset-0 h-full w-full object-cover" />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908] via-[#0a0908]/20 to-transparent" />
    {m.type === "video" && (
      <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-amber-400/70 bg-amber-400/10 backdrop-blur-md transition-transform duration-500 group-hover:scale-110">
        <Play className="h-5 w-5 fill-amber-300 text-amber-300" />
      </span>
    )}
    <span className="absolute bottom-5 left-5 right-5">
      <span className="font-mono-x text-[11px] uppercase tracking-[0.25em] text-amber-400">{m.type === "photo" ? "Photo" : "Vidéo"}</span>
      <span className="mt-1 block font-serif-it text-xl italic text-amber-100">{m.title}</span>
    </span>
  </button>
);

export const Gallery = () => {
  const [open, setOpen] = useState(null);
  const [media, setMedia] = useState([]);
  const c = useContent();
  useEffect(() => {
    api.get("/media").then((r) => setMedia(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  return (
    <section id="rituels" className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-36" data-testid="gallery-section">
      <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <Reveal><Eyebrow>04 — Démonstrations</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display text-3xl leading-tight text-amber-50 sm:text-4xl lg:text-5xl">
              Rituels &amp; Voyance Haute Puissance
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2} className="lg:col-span-5">
          <p className="text-base leading-relaxed text-stone-300">
            Découvrez les outils spirituels authentiques et les carrés mystiques utilisés lors de vos travaux discrets.
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <div className="mt-14 grid auto-rows-[240px] grid-cols-1 gap-5 md:grid-cols-4">
          <Photo src={pick(c, "img_candles", "/img/candles1.jpg")} label="Rituels de bougies d'attraction" className="md:col-span-2 md:row-span-2" testid="gallery-candles" />
          <Photo src={pick(c, "img_talismans", "/img/talisman2.jpg")} label="Objets & talismans africains" className="md:col-span-2" testid="gallery-talismans" />
          <button onClick={() => setOpen({ title: "Activation du carré mystique" })} data-testid="gallery-video-1"
            className="group relative overflow-hidden rounded-3xl border border-amber-900/40 text-left">
            <img src={pick(c, "img_carre", "/img/carre-mystique.jpg")} alt="Activation du carré mystique" className="absolute inset-0 h-full w-full object-cover opacity-40 transition-opacity duration-700 group-hover:opacity-60" />
            <div className="absolute inset-0 bg-[#0a0908]/50" />
            <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-amber-400/70 bg-amber-400/10 backdrop-blur-md transition-transform duration-500 group-hover:scale-110">
              <Play className="h-6 w-6 fill-amber-300 text-amber-300" />
            </span>
            <span className="absolute bottom-5 left-5 right-5">
              <span className="font-mono-x text-[11px] uppercase tracking-[0.25em] text-amber-400">Vidéo</span>
              <span className="mt-1 block font-serif-it text-xl italic text-amber-100">Activation du carré mystique</span>
            </span>
          </button>
          <button onClick={() => setOpen({ title: "Consultation de voyance en direct" })} data-testid="gallery-video-2"
            className="group relative overflow-hidden rounded-3xl border border-amber-900/40 text-left">
            <img src="/img/talisman1.jpg" alt="Consultation de voyance en direct" className="absolute inset-0 h-full w-full object-cover opacity-40 transition-opacity duration-700 group-hover:opacity-60" />
            <div className="absolute inset-0 bg-[#0a0908]/50" />
            <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-amber-400/70 bg-amber-400/10 backdrop-blur-md transition-transform duration-500 group-hover:scale-110">
              <Play className="h-6 w-6 fill-amber-300 text-amber-300" />
            </span>
            <span className="absolute bottom-5 left-5 right-5">
              <span className="font-mono-x text-[11px] uppercase tracking-[0.25em] text-amber-400">Vidéo</span>
              <span className="mt-1 block font-serif-it text-xl italic text-amber-100">Consultation de voyance en direct</span>
            </span>
          </button>
          <Photo src={pick(c, "img_cercle", "/img/cercle-mystique.jpg")} label="Le cercle mystique" className="md:col-span-2" testid="gallery-cercle" />
          <Photo src={pick(c, "img_carre", "/img/carre-mystique.jpg")} label="Le carré mystique" className="md:col-span-2" testid="gallery-carre" />
          {media.map((m) => <MediaTile key={m.id} m={m} onOpen={setOpen} />)}
        </div>
      </Reveal>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="border-amber-800/50 bg-[#12100e] text-amber-50" data-testid="media-dialog">
          <DialogTitle className="font-serif-it text-2xl italic text-amber-100">{open?.title}</DialogTitle>
          {open?.url ? (
            <video src={open.url} controls autoPlay className="aspect-video w-full rounded-xl bg-black" data-testid="media-player" />
          ) : (
            <div className="flex aspect-video items-center justify-center rounded-xl border border-dashed border-amber-700/50 text-center text-sm text-stone-300">
              Vidéo bientôt disponible.<br />Écrivez au Maître dans le chat pour une démonstration privée.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
